'use client'

import { Button } from '@repo/design-system-cf/components/button'
import { IconAdd } from '@repo/design-system-cf/components/icons'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { Link } from '@repo/web-ui/components/link'
import { isHistoryThumbnailPending } from './session-list-refresh'
import { toolRailClassName, toolRailThumbClassName } from './tool-frame'

export interface ToolSession {
  id: string
  thumbnailUrl?: string
  isGenerating?: boolean
}

export function HistoryThumbPendingOverlay({ label }: { label: string }) {
  return (
    <span
      role="status"
      aria-label={label}
      className="absolute inset-0 flex items-center justify-center bg-cf-grey-0/70"
    >
      <Spinner className="size-5" />
    </span>
  )
}

export function ToolSessionRail({
  label,
  newSessionLabel,
  processingLabel,
  homeHref,
  activeId,
  sessions,
  onPrefetch,
}: {
  label: string
  newSessionLabel: string
  processingLabel: string
  homeHref: string
  activeId?: string
  sessions: ToolSession[]
  onPrefetch?: (id: string) => void
}) {
  const isHome = !activeId

  return (
    <nav aria-label={label} className={toolRailClassName}>
      {isHome ? (
        <Button variant="primary" size="small" disabled aria-label={newSessionLabel}>
          <IconAdd className="h-5 w-5" />
        </Button>
      ) : (
        <Button variant="primary" size="small" asChild>
          <Link href={homeHref} component="Link" aria-label={newSessionLabel}>
            <IconAdd className="h-5 w-5" />
          </Link>
        </Button>
      )}

      {sessions.map((session, index) => {
        const href = `${homeHref}${session.id}`
        const isActive = session.id === activeId
        return (
          <Link
            key={session.id}
            href={href}
            component="Link"
            aria-current={isActive ? 'page' : undefined}
            onMouseEnter={() => {
              onPrefetch?.(session.id)
            }}
            className={toolRailThumbClassName(isActive)}
          >
            {session.thumbnailUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={session.thumbnailUrl}
                alt=""
                width={48}
                height={48}
                decoding="async"
                loading={index < 8 ? 'eager' : 'lazy'}
                className="size-full object-cover"
              />
            ) : (
              <span className="block size-full bg-cf-grey-100" />
            )}
            {isHistoryThumbnailPending(session) ? (
              <HistoryThumbPendingOverlay label={processingLabel} />
            ) : null}
          </Link>
        )
      })}
    </nav>
  )
}
